import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';

dayjs.extend(utc);
dayjs.extend(timezone);

export const SUBSCRIPTION_TIMEZONE = 'Australia/Brisbane';

/**
 * Returns the final millisecond of the Brisbane calendar date reached after
 * adding the configured number of days to the source timestamp.
 */
export function getBrisbaneEndOfDayAfter(validFrom: Date, daysToAdd: number): Date {
	return dayjs(validFrom).tz(SUBSCRIPTION_TIMEZONE).add(daysToAdd, 'day').endOf('day').toDate();
}

/**
 * Finite trial access lasts through the end of the Brisbane calendar date
 * reached by adding the subscription type's configured duration.
 */
export function getTrialAccessEnd(validFrom: Date, durationInDays: number): Date {
	return getBrisbaneEndOfDayAfter(validFrom, durationInDays);
}

export function getNonShorteningTrialAccessEnd(validFrom: Date, currentValidTo: Date, durationInDays: number): Date {
	const alignedValidTo = getTrialAccessEnd(validFrom, durationInDays);
	return alignedValidTo > currentValidTo ? alignedValidTo : currentValidTo;
}

/**
 * Upgrade maximums are zero-based inclusive day indexes. A maximum of 13
 * therefore expires at the end of the Brisbane date 14 days after purchase.
 */
export function getConfiguredWindowEnd(validFrom: Date, daysAfterPurchaseMax: number | null): Date | null {
	if (daysAfterPurchaseMax === null) {
		return null;
	}

	return getBrisbaneEndOfDayAfter(validFrom, daysAfterPurchaseMax + 1);
}

export function getLargestFiniteMaximum(maximums: Array<number | null>): number | null {
	return maximums.reduce<number | null>(
		(largest, maximum) => (maximum === null ? largest : Math.max(largest ?? maximum, maximum)),
		null,
	);
}

export function isWithinConfiguredMaximum(
	validFrom: Date,
	referenceDate: Date,
	daysAfterPurchaseMax: number | null,
	terminalMaximum: number | null,
): boolean {
	if (daysAfterPurchaseMax === null) {
		return true;
	}
	if (daysAfterPurchaseMax === terminalMaximum) {
		return referenceDate <= getConfiguredWindowEnd(validFrom, daysAfterPurchaseMax)!;
	}

	const exactEnd = new Date(validFrom.getTime() + (daysAfterPurchaseMax + 1) * 24 * 60 * 60 * 1000);
	return referenceDate < exactEnd;
}
